/**
 * Système de logging sécurisé pour l'environnement de production
 * Évite l'exposition de données sensibles en production
 */

// Configuration des niveaux de log selon l'environnement
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

// Liste des champs sensibles à masquer
const SENSITIVE_FIELDS = [
  'password', 'token', 'authorization', 'secret', 'key', 'api_key',
  'access_token', 'refresh_token', 'session_id', 'csrf_token',
  'credit_card', 'ssn', 'email', 'phone'
];

/**
 * Masque les données sensibles dans un objet
 * @param {any} data - Données à nettoyer
 * @returns {any} - Données nettoyées
 */
const sanitizeData = (data) => {
  if (typeof data !== 'object' || data === null) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    const keyLower = key.toLowerCase();
    const isSensitive = SENSITIVE_FIELDS.some(field => keyLower.includes(field));
    
    if (isSensitive) {
      sanitized[key] = '***MASKED***';
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Logger sécurisé
 */
class SecureLogger {
  static error(message, data = null) {
    if (IS_PRODUCTION) return;
    console.error(`[ERROR] ${message}`, data ? sanitizeData(data) : '');
  }

  static warn(message, data = null) {
    if (IS_PRODUCTION) return;
    console.warn(`[WARN] ${message}`, data ? sanitizeData(data) : '');
  }

  static info(message, data = null) {
    if (IS_PRODUCTION) return; // Pas de logs info en production
    console.info(`[INFO] ${message}`, data ? sanitizeData(data) : '');
  }

  static debug(message, data = null) {
    if (!IS_DEVELOPMENT) return; // Seulement en développement
    console.debug(`[DEBUG] ${message}`, data ? sanitizeData(data) : '');
  }

  /**
   * Log spécifique pour les appels API
   * @param {string} method - Méthode HTTP
   * @param {string} url - URL de l'API
   * @param {object} config - Configuration de la requête (sans headers sensibles)
   */
  static apiCall(method, url, config = {}) {
    if (IS_PRODUCTION) return;
    
    const sanitizedConfig = {
      ...config,
      headers: config.headers ? { 'Content-Type': config.headers['Content-Type'] } : {}
    };
    
    this.debug(`API ${method.toUpperCase()} ${url}`, sanitizedConfig);
  }

  /**
   * Log pour les erreurs d'authentification
   * @param {string} context - Contexte de l'erreur
   * @param {Error} error - Erreur survenue
   */
  static authError(context, error) {
    this.error(`Auth Error [${context}]`, {
      message: error.message,
      status: error.response?.status,
      // Ne pas logger error.response.data qui peut contenir des infos sensibles
    });
  }

  /**
   * Log pour les opérations CRUD réussies
   * @param {string} operation - Type d'opération (CREATE, UPDATE, DELETE)
   * @param {string} entity - Entité concernée
   * @param {string} id - ID de l'entité (optionnel)
   */
  static operation(operation, entity, id = null) {
    if (IS_PRODUCTION) return;
    this.info(`${operation} ${entity}${id ? ` (ID: ${id})` : ''} - Success`);
  }
}

export default SecureLogger;
